import { useAuth } from '@cibo/profile'
import { Markdown, ResponsiveDialog, SubmitButton } from '@cibo/ui'
import { Button, DialogActions, DialogContent, DialogTitle } from '@mui/material'
import { Form, Formik, useFormikContext } from 'formik'
import { useSnackbar } from 'notistack'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'
import { useSendImportMessage } from './useSendImportMessage'

type ImportFieldAlyticsDialogProps = {
  open: boolean
  onCancel(): void
  onSuccess(): void
}

const ImportFieldAlyticsForm = ({ open, onCancel, onSuccess }: ImportFieldAlyticsDialogProps) => {
  const { t } = useTranslation('@cibo/landmanager/ImportFieldAlyticsDialog')
  const { resetForm } = useFormikContext()
  const { persona } = useAuth()

  useEffect(() => {
    if (!open) {
      resetForm()
    }
  }, [open])

  return (
    <ResponsiveDialog open={open} onClose={onCancel}>
      <DialogTitle>{t('title')}</DialogTitle>

      <DialogContent sx={{ width: 385 }}>
        <Markdown>
          {persona === 'grower' ? t('cta', { context: 'participant' }) : t('managerCopyMd')}
        </Markdown>
      </DialogContent>

      <DialogActions>
        {persona === 'grower' ? (
          <>
            <Button variant="outlined" onClick={onCancel} data-testid="cancel-import-field-alytics">
              {t('cancel')}
            </Button>
            <SubmitButton testId="submit-import-field-alytics">{t('confirm')}</SubmitButton>
          </>
        ) : (
          <Button onClick={onCancel} variant="contained">
            {t('Ok')}
          </Button>
        )}
      </DialogActions>
    </ResponsiveDialog>
  )
}

export const ImportFieldAlyticsDialog = (props: ImportFieldAlyticsDialogProps) => {
  const { t } = useTranslation('@cibo/landmanager/ImportFieldAlyticsDialog')
  const { enqueueSnackbar } = useSnackbar()

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        importUser: yup.string(),
      }),
    []
  )

  const sendImportMessage = useSendImportMessage()

  const handleSubmitForm = (values: any) => {
    sendImportMessage.mutateAsync().then(() => {
      enqueueSnackbar(t('toast'), {
        variant: 'success',
      })
      props.onSuccess()
    })
  }

  const initialValues = { importUser: '' }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmitForm}
      enableReinitialize
    >
      <Form>
        <ImportFieldAlyticsForm {...props} />
      </Form>
    </Formik>
  )
}
